import { z } from "zod";

/**
 * E-posta şablon sözleşmesi (ADR-0011 E3, CLOS-316).
 *
 * Şablon parametreleri `packages/shared` içinde tip güvenli tanımlanır: hem sunucu hem
 * (ileride) istemci aynı sözleşmeyi görür. Harici şablon servisi kullanılmaz — metin
 * değişikliği code review ve sürüm geçmişi görsün.
 */

/** Desteklenen diller. Bugün tek dil var; alan şimdiden sözleşmede ki sonradan eklenmesin. */
export const MAIL_LOCALES = ["tr"] as const;
export type MailLocale = (typeof MAIL_LOCALES)[number];

export const welcomeMailParams = z.object({
  tenantName: z.string().min(1, "Firma adı zorunludur"),
});
export type WelcomeMailParams = z.infer<typeof welcomeMailParams>;

export const passwordResetMailParams = z.object({
  resetUrl: z.url("Sıfırlama bağlantısı geçerli bir adres olmalıdır"),
  /** Bağlantının kaç dakika geçerli olduğu — kullanıcı aciliyeti bilsin. */
  expiresInMinutes: z.number().int().positive(),
});
export type PasswordResetMailParams = z.infer<typeof passwordResetMailParams>;

/**
 * Şablon kimlikleri. Sürüm ayrı bir alandır: bir şablonun metni değiştiğinde kimliği
 * DEĞİŞMEZ, sürümü artar. Böylece "hangi metin gönderildi?" sorusu geçmişe dönük
 * cevaplanabilir kalır.
 */
export const emailVerificationMailParams = z.object({
  verifyUrl: z.url("Doğrulama bağlantısı geçerli bir adres olmalıdır"),
  /** Bağlantının kaç saat geçerli olduğu — kullanıcı aciliyeti bilsin. */
  expiresInHours: z.number().int().positive(),
});
export type EmailVerificationMailParams = z.infer<typeof emailVerificationMailParams>;

/**
 * Hesaba yeni bir giriş yöntemi eklendiğinde gider (CLOS-357).
 *
 * Sağlayıcı ADI dışında hiçbir sağlayıcı verisi taşımaz: e-posta metni de bir sızıntı
 * yüzeyidir ve posta kutusu saldırganın elindeyse okunur.
 */
export const identityLinkedMailParams = z.object({
  provider: z.string().min(1, "Sağlayıcı adı zorunludur"),
});
export type IdentityLinkedMailParams = z.infer<typeof identityLinkedMailParams>;

/**
 * Hesap durumu değiştiğinde gider (CLOS-282).
 *
 * Yalnız YENİ durumu taşır; gerekçe metni taşımaz. Gerekçe iç kayıt için yazılır ve
 * serbest metindir — posta kutusu saldırganın elindeyse okunacak bir yere serbest metin
 * koymak yeni bir sızıntı yüzeyi açardı.
 */
export const accountStatusChangedMailParams = z.object({
  status: z.string().min(1, "Durum zorunludur"),
});
export type AccountStatusChangedMailParams = z.infer<typeof accountStatusChangedMailParams>;

/**
 * Şifre değiştiğinde gider (CLOS-64). Hiçbir parametre TAŞIMAZ: metin tek ve sabit,
 * taşınacak veri yok. Boş bir nesne, "ileride bir şey eklenirse" diye alan açmaktan iyi.
 */
export const passwordChangedMailParams = z.object({});
export type PasswordChangedMailParams = z.infer<typeof passwordChangedMailParams>;

/**
 * Profilde kritik bir alan değiştiğinde gider (CLOS-65).
 *
 * Yalnız DEĞİŞEN ALANLARIN adlarını taşır, yeni değerleri değil: posta kutusu saldırganın
 * elindeyse okunur ve yeni telefon numarasını oraya yazmak yeni bir sızıntı olurdu.
 */
export const profileChangedMailParams = z.object({
  fields: z.array(z.string()).min(1, "En az bir alan gereklidir"),
});
export type ProfileChangedMailParams = z.infer<typeof profileChangedMailParams>;

/**
 * Rol yetkileri değişti bildirimi (CLOS-86 `[029]`).
 *
 * Kart: *"etkilenen kullanıcılar ve yetkili yöneticiler bilgilendirilir."* Kullanıcı bir
 * sabah ürünün yarısını göremediğinde sebebini bilmelidir; sessiz bir yetki daralması,
 * arıza ile ayırt edilemez.
 */
export const roleChangedMailParams = z.object({
  roleName: z.string().min(1, "Rol adı gereklidir"),
  added: z.array(z.string()),
  removed: z.array(z.string()),
});
export type RoleChangedMailParams = z.infer<typeof roleChangedMailParams>;

/**
 * Organizasyon daveti bildirimi (CLOS-73 `[034]`).
 *
 * Bağlantı ham token taşıyor ve yalnız e-postada bulunuyor: sunucuda özeti duruyor.
 */
export const organizationInviteMailParams = z.object({
  organizationName: z.string().min(1, "Organizasyon adı gereklidir"),
  inviteUrl: z.url("Geçerli bir bağlantı gereklidir"),
  expiresAt: z.string().min(1, "Son geçerlilik gereklidir"),
});
export type OrganizationInviteMailParams = z.infer<typeof organizationInviteMailParams>;

/**
 * Üyelik durumu değişti bildirimi (CLOS-77 `[038]`).
 *
 * Kart: *"bildirim ve audit oluşur."* Erişimi bir sabah kaybolan kullanıcı sebebini
 * bilmelidir; sessiz bir askı, arıza ile ayırt edilemez ve destek kaydına dönüşür.
 *
 * Gerekçenin **etiketi** taşınıyor, kodu değil: `security_review` kullanıcı için bir şey
 * ifade etmiyor, "Güvenlik incelemesi" ediyor. Serbest metin NOT taşınmıyor — yönetici
 * notu iç bağlamdır ve e-postaya konsaydı kullanıcı hakkında yazılmış bir metin, kimsenin
 * öyle olduğunu düşünmediği bir anda kendisine gönderilirdi.
 */
export const membershipStatusChangedMailParams = z.object({
  organizationName: z.string().min(1, "Organizasyon adı gereklidir"),
  status: z.string().min(1, "Durum zorunludur"),
  reasonLabel: z.string().min(1, "Gerekçe gereklidir"),
});
export type MembershipStatusChangedMailParams = z.infer<typeof membershipStatusChangedMailParams>;

/**
 * Üyeliğin ROLÜ değişti bildirimi (CLOS-89 `[039]`).
 *
 * `roleChanged` ile karıştırılmamalı: o, bir ROLÜN yetkilerinin değişmesidir ve o rolü
 * taşıyan herkese gider. Bu ise TEK kullanıcının rolünün değişmesi. İkisini tek şablona
 * sıkıştırmak, kullanıcıya "yetkileriniz değişti" deyip hangisinin olduğunu söylememek
 * olurdu.
 *
 * Yetki listesi TAŞINMIYOR: yeni rolün yetkilerini e-postaya dökmek, kullanıcının
 * göremediği bir yüzeyin haritasını posta kutusuna koymak olurdu. Rol ADI yeterli.
 */
export const membershipRoleChangedMailParams = z.object({
  organizationName: z.string().min(1, "Organizasyon adı gereklidir"),
  roleName: z.string().min(1, "Rol adı gereklidir"),
});
export type MembershipRoleChangedMailParams = z.infer<typeof membershipRoleChangedMailParams>;

export const MAIL_TEMPLATES = {
  welcome: "welcome",
  passwordReset: "password-reset",
  emailVerification: "email-verification",
  identityLinked: "identity-linked",
  accountStatusChanged: "account-status-changed",
  passwordChanged: "password-changed",
  profileChanged: "profile-changed",
  roleChanged: "role-changed",
  organizationInvite: "organization-invite",
  membershipStatusChanged: "membership-status-changed",
  membershipRoleChanged: "membership-role-changed",
} as const;
export type MailTemplateId = (typeof MAIL_TEMPLATES)[keyof typeof MAIL_TEMPLATES];
